import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  CommentItem,
  CommentStatus,
  ListCommentsQuery,
  Paginated,
  StaffReplyInput,
} from '@news-cms/shared';
import { ApiError, api } from './api';
import { toast } from '@/components/ui/toaster';

function qs(filter: Partial<ListCommentsQuery>): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filter)) {
    if (v === undefined || v === null || v === '') continue;
    params.set(k, String(v));
  }
  return params.toString();
}

export const commentsListQueryOptions = (filter: Partial<ListCommentsQuery>) =>
  queryOptions({
    queryKey: ['comments', 'list', filter] as const,
    queryFn: () => api<Paginated<CommentItem>>(`/api/admin/comments?${qs(filter)}`),
  });

export interface CommentThread {
  parent: CommentItem | null;
  target: CommentItem;
  replies: CommentItem[];
}

export const commentThreadQueryOptions = (id: string | null) =>
  queryOptions({
    queryKey: ['comments', 'thread', id] as const,
    queryFn: () => api<CommentThread>(`/api/admin/comments/${id ?? ''}/thread`),
    enabled: id !== null,
    staleTime: 30_000,
  });

export function useUpdateCommentStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: CommentStatus }) => {
      await api<{ ok: true }>(`/api/admin/comments/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      });
      return { id, status };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['comments', 'list'] });
      toast.success('อัปเดตสถานะแล้ว');
    },
    onError: (e) => toast.error(commentErrorMessage(e)),
  });
}

/** Post a staff reply on a comment. Server auto-approves (status:'approved')
 *  and stamps the current admin as the author. */
export function useStaffReply() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ postId, ...input }: StaffReplyInput & { postId: string }) => {
      await api<{ comment: CommentItem }>(`/api/admin/comments/${postId}/reply`, {
        method: 'POST',
        body: JSON.stringify(input),
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['comments', 'list'] });
      toast.success('ตอบกลับแล้ว');
    },
    onError: (e) => toast.error(commentErrorMessage(e)),
  });
}

export function useDeleteComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api<{ ok: true }>(`/api/admin/comments/${id}`, { method: 'DELETE' });
      return id;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['comments', 'list'] });
      toast.success('ลบ comment เรียบร้อย');
    },
    onError: (e) => toast.error(commentErrorMessage(e)),
  });
}

/**
 * Bulk: run N parallel single-row PATCHes (Promise.allSettled), aggregate
 * toast — same pattern as useBulkPostAction.
 */
export function useBulkUpdateCommentStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ ids, status }: { ids: string[]; status: CommentStatus }) => {
      const results = await Promise.allSettled(
        ids.map((id) =>
          api(`/api/admin/comments/${id}/status`, {
            method: 'PATCH',
            body: JSON.stringify({ status }),
          }),
        ),
      );
      const fulfilled = results.filter((r) => r.status === 'fulfilled').length;
      const rejected = results.length - fulfilled;
      return { fulfilled, rejected, status };
    },
    onSuccess: ({ fulfilled, rejected, status }) => {
      void qc.invalidateQueries({ queryKey: ['comments', 'list'] });
      const label =
        status === 'approved'
          ? 'อนุมัติ'
          : status === 'rejected'
            ? 'ปฏิเสธ'
            : status === 'spam'
              ? 'spam'
              : status;
      if (rejected === 0) toast.success(`${label} ${fulfilled} comment เรียบร้อย`);
      else toast.warning(`${label}สำเร็จ ${fulfilled} · ผิดพลาด ${rejected}`);
    },
    onError: (e) => toast.error(commentErrorMessage(e)),
  });
}

export function commentErrorMessage(e: unknown): string {
  if (!(e instanceof ApiError)) return 'เกิดข้อผิดพลาด';
  switch (e.code) {
    case 'COMMENT_NOT_FOUND':
      return 'ไม่พบ comment';
    case 'FORBIDDEN':
      return 'คุณไม่มีสิทธิ์';
    default:
      return e.message || 'เกิดข้อผิดพลาด';
  }
}
